import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import StatsCard from '../../components/common/StatsCard';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import {
  TrendingUp,
  DollarSign,
  Users,
  Activity,
  Download,
  Calendar,
  ChevronDown,
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

interface AnalyticsData {
  totalRevenue: number;
  activeUsers: number;
  totalTrades: number;
  conversionRate: number;
  monthlyGrowth: number;
  topPerformers: Array<{
    name: string;
    profit: number;
    trades: number;
  }>;
}

const Analytics = () => {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>(
    '30d'
  );

  const analyticsData: AnalyticsData = {
    totalRevenue: 2847500,
    activeUsers: 1247,
    totalTrades: 8934,
    conversionRate: 68.4,
    monthlyGrowth: 12.5,
    topPerformers: [
      { name: 'John Smith', profit: 45230, trades: 234 },
      { name: 'Sarah Johnson', profit: 38920, trades: 189 },
      { name: 'Mike Williams', profit: 32150, trades: 156 },
    ],
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Analytics Dashboard"
          description="Track your trading performance and business metrics"
        />
      </div>
      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {timeRange === '7d'
                ? '7 Days'
                : timeRange === '30d'
                  ? '30 Days'
                  : timeRange === '90d'
                    ? '90 Days'
                    : '1 Year'}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {(['7d', '30d', '90d', '1y'] as const).map(range => (
              <DropdownMenuItem
                key={range}
                onClick={() => setTimeRange(range)}
                className={timeRange === range ? 'bg-accent' : ''}
              >
                {range === '7d'
                  ? '7 Days'
                  : range === '30d'
                    ? '30 Days'
                    : range === '90d'
                      ? '90 Days'
                      : '1 Year'}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" className="flex items-center gap-2">
          <Download className="h-4 w-4" /> Export Report
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Revenue"
          value={formatCurrency(analyticsData.totalRevenue)}
          change={`+${analyticsData.monthlyGrowth}% from last month`}
          icon={DollarSign}
        />

        <StatsCard
          title="Active Users"
          value={analyticsData.activeUsers.toLocaleString()}
          change="+8.2% from last month"
          icon={Users}
        />

        <StatsCard
          title="Total Trades"
          value={analyticsData.totalTrades.toLocaleString()}
          change="-2.4% from last month"
          icon={Activity}
        />

        <StatsCard
          title="Conversion Rate"
          value={`${analyticsData.conversionRate}%`}
          change="+3.1% from last month"
          icon={TrendingUp}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Top Performers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analyticsData.topPerformers.map((performer, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-semibold text-blue-600">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{performer.name}</p>
                      <p className="text-sm text-gray-500">
                        {performer.trades} trades
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">
                      {formatCurrency(performer.profit)}
                    </p>
                    <Badge variant="outline" className="text-xs">
                      Top {index + 1}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">New High Score</p>
                    <p className="text-sm text-gray-500">
                      EUR/USD reached new high
                    </p>
                  </div>
                </div>
                <span className="text-xs text-gray-500">2 min ago</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">New User Registration</p>
                    <p className="text-sm text-gray-500">
                      5 new traders joined
                    </p>
                  </div>
                </div>
                <span className="text-xs text-gray-500">15 min ago</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <DollarSign className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">Commission Payout</p>
                    <p className="text-sm text-gray-500">$12,450 processed</p>
                  </div>
                </div>
                <span className="text-xs text-gray-500">1 hour ago</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
